<template>
	<div id="exams">
		<v-row no-gutters class="fill-height">
			<!-- sidebar -->
			<v-col lg="2" sm="4" cols="12" class="pa-3">
				<div class="font-weight-bold text-h6">الاختبارات</div>

				<!-- add new question -->
				<v-btn
					depressed
					:to="{
						name: 'exam',
						query: {
							classId: $route.query.classId,
							courseId: $route.query.courseId,
							conceptId: $route.query.conceptId,
						}
					}"
					width="100%"
					color="primary"
					class="mt-3"
				>
					<v-icon class="me-2">mdi-plus</v-icon>
					إنشاء اختبار جديد
				</v-btn>

				<div class="mt-4">
					<p class="mb-2">البيانات الأساسية</p>

					<!-- class -->
					<v-autocomplete
						v-model="classId"
						:items="classes"
						item-text="name"
						item-value="id"
						label="الصف"
						:disabled="loading"
						outlined
						dense
						hide-details
						class="mb-3"
						@change="
							courseId = null;
							$router.replace(
								$event
									? { query: { ...$route.query, classId, courseId: courseId || undefined } } : { query: {} }
							);
						"
					/>

					<!-- course -->
					<v-autocomplete
						v-if="tab === 1"
						v-model="courseId"
						:items="courses"
						item-text="title"
						item-value="id"
						label="الشعبة"
						:loading="loadingClassData"
						:disabled="loading"
						outlined
						dense
						hide-details
						class="mb-3"
						@change="$router.replace({ query: { ...$route.query, courseId: $event || undefined } })"
					/>

					<!-- concept -->
					<v-autocomplete
						v-model="conceptId"
						:items="concepts"
						item-text="name"
						item-value="id"
						label="المحور"
						:loading="loadingClassData"
						:disabled="loading"
						outlined
						dense
						hide-details
						class="mb-3"
						@change="$router.replace({ query: { ...$route.query, conceptId: $event || undefined } })"
					/>
				</div>
			</v-col>
			<v-divider v-if="$vuetify.breakpoint.smAndUp" vertical/>

			<!-- content -->
			<v-col lg="10" sm="8" cols="12">
				<!-- breadcrumbs -->
				<div
					class="d-flex flex-wrap flex-sm-row flex-column-reverse justify-space-between align-sm-center pa-3"
				>
					<!-- breadcrumbs -->
					<bee-breadcrumbs
						:items=" [
							{
								to: null,
								disabled: false,
								icon: 'mdi-bookshelf',
								text: 'الاختبارات'
							},
							{
								to: null,
								disabled: true,
								text: ['كل الاختبارات', 'اختبارات الشعب'][tab]
							}
						]"
						class="mt-sm-0 mt-3"
					/>
				</div>

				<v-divider/>

				<div class="px-3 py-2">
					<!-- tabs -->
					<v-tabs
						v-model="tab"
						hide-slider
						class="mb-2"
						@change="$router.replace({ query: { ...$route.query, tab } })"
					>
						<v-tab class="rounded overflow-hidden">بنك الاختبارات</v-tab>
						<v-tab disabled class="rounded overflow-hidden">اختبارات الشعب</v-tab>
					</v-tabs>

					<!-- table -->
					<!-- tab === 0 ? exams : classExams -->
					<bee-handy-table
						v-show="$route.query.classId"
						ref="table"
						:headers="tab === 0 ? examsHeaders : classExamsHeaders"
						:items="[]"
						:items-per-page="50"
						:loading="loading"
						fixed-header
						:height="$vuetify.breakpoint.height - 190"
						:server-items-length="totalItems"
						:data-table-props="{ disableSort: true }"
						dense
						zebra
						with-columns-divider
						pagination-on-scroll
						pagination-on-scroll-server-items
						shown-rows-text
						hide-default-footer
						table-class="overflow-hidden"
						@pagination-on-scroll:reach-last-row="fetchTests"
					>
						<!-- included exams -->
						<template v-slot:item.includedExams="{ item }">
							<v-chip-group v-if="(item.topics || []).length" column>
								<v-chip
									v-for="(c, i) in item.topics.map(c => c.topic)"
									:key="i"
									small
								>
									<!-- :class="c.concept.color" -->
									{{`${c.parent.name} / ${c.name}`}}
								</v-chip>
							</v-chip-group>
						</template>

						<!-- included exams -->
						<template v-slot:item.examStatus="{ item }">
							<v-chip
								small
								class="rounded-sm"
								:class="{
									primary: item.examStatus === examStatus.new,
									warning: item.examStatus === examStatus.inProgress,
									'success darken-1': item.examStatus === examStatus.done,
								}"
							>
								{{examStatuses.find(c => c.id === item.examStatus)?.name}}
							</v-chip>
						</template>

						<!-- actions -->
						<template v-slot:item.actions="{ item }">
							<div class="d-flex">
								<!-- preview -->
								<v-btn
									icon
									small
									class="me-1"
									:to="{ name: 'exam-preview', params: { examId: item.examTestId } }"
								>
									<v-icon>$preview</v-icon>
								</v-btn>

								<!-- printer -->
								<v-btn
									icon
									small
									class="me-1"
									:to="{
										name: 'exam-preview',
										params: { examId: item.examTestId },
										query: { isPrint: 1 }
									}"
								>
									<v-icon>$printer</v-icon>
								</v-btn>
								
								<!-- marks table -->
								<!-- <v-btn
									icon
									small
									disabled
									class="me-1"
									@click="printMarksTable"
								>
									<v-icon>mdi-clipboard-list-outline</v-icon>
								</v-btn> -->

								<v-divider vertical class="me-1"/>
								
								<!-- edit -->
								<v-btn
									icon
									small
									class="me-1"
									:to="{
										name: 'exam',
										params: { id: item.examTestId },
										query: { classId: typeof classId === 'number' ? classId : undefined }
									}"
								>
									<v-icon>$edit</v-icon>
								</v-btn>

								<!-- delete -->
								<v-btn icon small disabled>
									<v-icon color="error">$delete</v-icon>
								</v-btn>
							</div>
						</template>
					</bee-handy-table>

					<p v-if="!$route.query.classId" class="text-center text-body-1 mt-6 mb-0">يرجى اختيار صف لعرض الامتحانات</p>
				</div>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import moment from 'moment';
import { examStatus, examStatuses } from '@/helpers/enums';
import { mapState } from 'vuex';
import { generateMarksTable } from '@/helpers/generate-html';
import printJS from 'print-js';
export default {
	name: 'QuestionsBank',

	data() {
		return {
			loading: false,
			
			examStatus,
			examStatuses,

			loadingClassData: false,
			courses: [],
			concepts: [],

			totalItems: 0,
			
			tab: +this.$route.query.tab || 0,
			classId: null,
			conceptId: +null,
			courseId: null,
			
			subjectsTree0: [
				{
					id: '1',
					name: 'Reading',
					color: 'color-1',
					children: [
						{ id: '1-1', name: 'Reading in phrases', value: 'readingInPhrases' },
						{ id: '1-2', name: 'Pace', value: 'pace' },
						{ id: '1-3', name: 'Syntax', value: 'syntax' },
						{ id: '1-4', name: 'Self-corrections', value: 'selfCorrections' },
						{ id: '1-5', name: 'Intonation', value: 'intonation' },
						{ id: '1-6', name: 'Letter naming fluency', value: 'letterNamingFluency' },
						{ id: '1-7', name: 'Slight word fluency', value: 'slightWordFluency' },
						{ id: '1-8', name: 'Comprehension', value: 'comprehension' },
					]
				},
				{
					id: '2',
					name: 'Phonics awareness',
					color: 'color-2',
					children: [
						{ id: '2-1', name: 'Phoneme categorization', value: 'phonemeCategorization' },
						{ id: '2-2', name: 'Phoneme blending', value: 'phonemeBlending' },
						{ id: '2-3', name: 'Phoneme segmentation', value: 'phonemeSegmentation' },
						{ id: '2-4', name: 'Phoneme segmentation', value: 'phonemeSegmentation' },
					],
				},
				{
					id: '3',
					name: 'Readers to writers',
					color: 'color-3',
					children: [
						{ id: '3-1', name: 'Spelling', value: 'spelling' },
						{ id: '3-2', name: 'Vocabulary', value: 'vocabulary' },
						{ id: '3-3', name: 'Ideas and content', value: 'ideasAndContent' },
						{ id: '3-4', name: 'Grammar', value: 'grammar' },
						{ id: '3-5', name: 'Structural analysis', value: 'structuralAnalysis' },
						{ id: '3-6', name: 'Writing', value: 'writing' },
					],
				},
				{
					id: '4',
					name: 'Listening and speaking',
					color: 'color-4',
					children: [
						{ id: '4-1', name: 'Interactive read aloud', value: 'interactiveReadAloud' },
						{ id: '4-2', name: 'Retelling cards', value: 'retellingCards' },
					],
				},
				{
					id: '5',
					name: 'Social and study skills',
					color: 'color-5',
					children: [
						{ id: '5-1', name: 'Participation', value: 'participation' },
						{ id: '5-2', name: 'Completion of work', value: 'completionOfWork' },
						{ id: '5-3', name: 'Concentration', value: 'concentration' },
						{ id: '5-4', name: 'Self direction', value: 'selfDirection' },
						{ id: '5-5', name: 'Cooperation with others', value: 'cooperationWithOthers' },
						{ id: '5-6', name: 'Behavior', value: 'behavior' },
						{ id: '5-7', name: 'Presentation of assignments', value: 'presentationOfAssignments' },
					],
				},
				{
					id: '6',
					name: 'Movement video',
					color: 'color-6',
					children: [
						{ id: '6-1', name: 'Movement video', value: 'movementVideo' }
					]
				}
			],
		}
	},

	watch: {
		'$route.query': {
			handler(val) {
				if (val.classId) {
					this.classId = +val.classId;
					this.fetchClassData(+val.classId);
				} else this.classId = null;
				
				this.conceptId = +val.conceptId || null;
				this.courseId = +val.courseId || null;
				
				if (this.classId || this.conceptId || this.courseId) {
					this.fetchTests();
				}
			},
			immediate: true
		},
	},

	computed: {
		...mapState({
			classes: state => state.classes.classes,
		}),

		examsHeaders() {
			const headers = [
				{
					text: 'اسم الاختبار',
					name: 'examName',
					value: 'name',
					width: '25%',
				},
				{
					text: 'الاختبارات المضمنة',
					name: 'includedExams',
					value: 'includedExams',
				},
				{
					text: '',
					width: 100,
					name: 'actions',
					value: 'actions',
				},
			];

			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass = (headers[i].columnClass ? headers[i].columnClass + ' ' : '') + 'px-2'
				headers[i].class = (headers[i].class ? headers[i].class + ' ' : '') + 'color-1-light';
			}

			return headers;
		},

		classExamsHeaders() {
			const headers = [
				{
					text: 'موعد الاختبار',
					name: 'examDate',
					value: 'examDate',
					width: 120,
					columnClass: 'text-pre-line',
					setValueExpr: val =>
						val.length === 2
							? `${moment(val[0]).format('YYYY-MM-DD')}
								${moment(val[0]).format('hh:mm a').replace(/am|pm/, (m) => ({ am: 'ص', pm: 'م' })[m])}- \
								${moment(val[1]).format('hh:mm a').replace(/am|pm/, (m) => ({ am: 'ص', pm: 'م' })[m])}
							`
							: null
				},
				{
					text: 'الاختبارات المضمنة',
					name: 'includedExams',
					value: 'includedExams',
				},
				{
					text: 'حالة الاختبار',
					name: 'examStatus',
					value: 'examStatus',
					align: 'center',
					width: 80,
					columnClass: 'text-no-wrap',
				},
				{
					text: 'الناجحين',
					name: 'examSuccess',
					value: 'examSuccess',
					align: 'center',
					width: 60
				},
				{
					text: 'الراسبين',
					name: 'examFail',
					value: 'examFail',
					align: 'center',
					width: 60
				},
				{
					text: 'نسبة النجاح',
					name: 'examSuccessRate',
					value: 'examSuccessRate',
					align: 'center',
					width: 90,
					setValueExpr: val => `${val || 0}%`
				},
				{
					text: '',
					width: 100,
					name: 'actions',
					value: 'actions',
				},
			];

			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass = (headers[i].columnClass ? headers[i].columnClass + ' ' : '') + 'px-2'
				headers[i].class = (headers[i].class ? headers[i].class + ' ' : '') + 'color-1-light';
			}

			return headers;
		},

		exams() {
			return Array.from({ length: 200 }).map((c, i) => ({
				examName: 'اختبار ' + (i + 1),
				includedExams: Array.from({ length: Math.round(Math.random() * 6) + 1 }).map(e => {
					const concept = this.subjectsTree0[Math.floor(Math.random() * this.subjectsTree0.length)];
					const exam = concept.children[Math.floor(Math.random() * concept.children.length)];

					return { concept, exam }
				}),
			}))
		},
		classExams() {
			return Array.from({ length: 200 }).map(() => ({
				examDate: [moment().toISOString(), moment().add(2, 'hours').toISOString()],
				includedExams: Array.from({ length: Math.round(Math.random() * 6) + 1 }).map(e => {
					const concept = this.subjectsTree0[Math.floor(Math.random() * this.subjectsTree0.length)];
					const exam = concept.children[Math.floor(Math.random() * concept.children.length)];

					return { concept, exam }
				}),
				examStatus:
					[examStatus.new, examStatus.inProgress, examStatus.done][Math.floor(Math.random() * 3)],
				examSuccess: Math.floor(Math.random() * 100),
				examFail: Math.floor(Math.random() * 100),
				examSuccessRate: Math.floor(Math.random() * 100),
			}))
		},
	},

	methods: {
		fetchTests(newOptions) {
			const query = {}
			if (this.$route.query.classId) {
				query.specialtyId = +this.$route.query.classId;
			}
			if (this.$route.query.conceptId) {
				query.conceptId = +this.$route.query.conceptId;
			}
			if (this.$route.query.courseId) {
				query.courseId = +this.$route.query.courseId;
			}
			
			let page = 1;
			if (newOptions) {
				page = newOptions.page || 1;
			} else if (this.$refs.table) {
				this.$refs.table.resetShownRows();
			}

			this.loading = true;
			const actionName = this.tab === 0 ? 'fetchAll' : 'fetchAllByCourse';
			this.$store.dispatch(`exams/${actionName}`, {
				...query,
				page,
				perPage: 50
			}).then(({ data, total }) => {
				this.$refs.table.addScrollItems(data);
				this.totalItems = total;
			}).finally(() => {
				this.loading = false;
			});
		},

		fetchClassData(classId) {
			this.loadingClassData = true;
			this.$store.dispatch('classes/fetchClassData', {
				classId,
			}).then(({ courses, concepts }) => {
				this.courses = courses;
				this.concepts = concepts;
			}).finally(() => {
				this.loadingClassData = false;
			});
		},

		printMarksTable() {
			const data = Array.from({ length: Math.round(Math.random() * 100) }).map((c, i) => ({
				question: i + 1,
				answer: Math.floor(Math.random() * 5),
				contentFocus: [
					'Reading: Pace',
					'Reading: Syntax',
					'Reading: Self-corrections',
					'Phonics awareness: Phoneme blending',
					'Readers to writers: Spelling',
					'Readers to writers: Vocabulary',
				][Math.floor(Math.random() * 6)],
				complexity: [
					'Easy',
					'Medium',
					'Hard',
				][Math.floor(Math.random() * 3)]
			}));
			
			const html = generateMarksTable(data);
			printJS({
				printable: html,
				type: 'raw-html',
			})
		},
	},
}
</script>

<style lang="scss">
#exams {
	height: 100%;
}
</style>